// Fragen mit Zusammenhang in der Doku-App (E3, S6; CLAUDE.md, Grundsatz 9):
//
//  - `FragenBand`: das Band in Lime oben auf der Startseite – und kleiner
//    oben in der Besichtigung –, „2 offene Fragen aus dem Büro – antworten“.
//    Gernots Entscheidung 2: kein Web-Push, dafür oben, sichtbar, nicht zu
//    übersehen. Ein Tipp führt an die älteste Frage, die auf den Verkäufer
//    wartet, und rollt sie in die Mitte (`frageAnspringen`).
//  - `FragenAbschnitt`: die Fragen an einem Stück – an der Besichtigung oben
//    in der Übersicht, an der Station oben in der Station, am Foto unter dem
//    Foto, an der Notiz bei der Notiz –, jede mit Fragendem, Zeit, Text, den
//    Antworten darunter und dem Stand; darunter „Antworten“ (Textfeld,
//    diktieren geht über die Tastatur – seit 22.10.2026 darunter „Wie
//    diktiere ich?“) und „Erledigt“. Dazu „Frage stellen“:
//    Auch der Verkäufer fragt – oder berichtigt, was nach dem Senden falsch
//    ist („Hausnummer ist 12a“, Gernot, 20.10.2026).
//
// Das Aussehen ist ein eigenes Muster, in der Doku-App und im CRM gleich
// (Gernot beim Augentest E3, 21.10.2026: „ein eigenes cooles Design und
// Schema … dezent, aber nicht zu übersehen“): „Frage stellen“ als weiße
// Pille mit dem Zeichen – ein Kreis in Kohle, darin das Fragezeichen in
// Lime; eine Frage samt Antworten als Karte mit Streifen links (Lime offen,
// grau erledigt), Kreis mit Initialen, Name, Zeit und Chip; keine
// Erklär-Absätze. Die Formen stehen in ../doku.css.
//
// Alles geht ohne Netz: gespeichert am Gerät, hinaus über den Postausgang,
// „wartet“ wie bei allem anderen (../gespraech.ts).

import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { BUERO_GRENZEN, type FrageBezug, type NutzerKurz } from "@happy-energy/kern";
import { Chip, DiktatHilfe, Ikon, toastZeigen } from "@happy-energy/oberflaeche";
import { antworten, frageStandAendern, frageStellen, gespraechText, wartetAufMich, type FrageAmGeraet, type FrageOrt } from "../gespraech.ts";
import { wannZeigen } from "../einsicht.ts";
import { anzahl, speichernFehlgeschlagen } from "./gemeinsam.tsx";

// --- Anspringen: das Band führt an die Frage, nicht nur auf die Seite ----------------------------

/** Die Frage, die nach dem nächsten Seitenwechsel in die Mitte rollen soll. */
let ziel: string | null = null;

/**
 * Nach dem Tipp aufs Band: die Frage in die Mitte rollen. Steht sie auf der
 * Seite, die schon offen ist, gleich; sonst, sobald sie auf der neuen Seite
 * erscheint (`FrageKarte`). Die Seite selbst springt beim Wechsel nach oben
 * (Doku.tsx) – deshalb rollt die Karte erst einen Augenblick danach.
 */
export function frageAnspringen(frageId: string, adresse: string): void {
  if (window.location.hash === adresse) {
    document.getElementById(`frage-${frageId}`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    return;
  }
  ziel = frageId;
}

// --- Das Band -------------------------------------------------------------------------------

/**
 * „2 offene Fragen aus dem Büro – antworten“: groß, in Lime, ein Tipp führt
 * an die älteste. `unter`: wo sie steht (Kunde, Station, Foto). `klein`: in
 * der Besichtigung, die schon offen ist.
 */
export function FragenBand({ zahl, erste, unter, klein = false }: { zahl: number; erste: { frageId: string; ort: FrageOrt }; unter: string; klein?: boolean }) {
  if (zahl === 0) return null;
  return (
    <a
      href={erste.ort.adresse}
      className={`fragen-band${klein ? " klein" : ""}`}
      onClick={(e) => {
        if (window.location.hash === erste.ort.adresse) e.preventDefault();
        frageAnspringen(erste.frageId, erste.ort.adresse);
      }}
    >
      <span className="fragen-band-zeichen" aria-hidden="true">
        <Ikon name="frage" />
      </span>
      <span className="fragen-band-wort">
        <b>{anzahl(zahl, "1 offene Frage aus dem Büro – antworten", "# offene Fragen aus dem Büro – antworten")}</b>
        <span>{unter}</span>
      </span>
      <Ikon name="rechts" />
    </a>
  );
}

// --- Bausteine des Musters ------------------------------------------------------------------------

/**
 * Das Zeichen der Fragen: ein Kreis in Kohle, darin das Fragezeichen in Lime.
 * Seit E3b (O81 a) nur das Fragezeichen im Kreis – vorher lag darin noch der
 * Kreis des Symbols, und es wirkte nicht mittig.
 */
function FrageZeichen() {
  return (
    <span className="frage-zeichen" aria-hidden="true">
      <Ikon name="fragezeichen" />
    </span>
  );
}

/**
 * Die Marke an einem Stück mit offenen Fragen: die kleine Pille in Lime mit
 * dem Zeichen, daneben „1 offene Frage“. Ohne Worte nur das Zeichen – an der
 * Foto-Kachel, für Worte ist sie zu klein (`titel` sagt es dann beim Zeigen).
 */
export function FrageMarke({ children, titel }: { children?: ReactNode; titel?: string }) {
  return (
    <span className={`frage-marke${children ? "" : " nur-zeichen"}`} title={titel}>
      <FrageZeichen />
      {children}
    </span>
  );
}

/** Zwei Buchstaben für den Kreis vor dem Namen: „Günther Büro“ → „GB“, „Vera“ → „VE“. */
export function initialen(name: string): string {
  const woerter = name.trim().split(/\s+/).filter(Boolean);
  const erstes = [...(woerter[0] ?? "")];
  const letztes = woerter.length > 1 ? [...(woerter[woerter.length - 1] ?? "")] : [];
  const zeichen = letztes.length > 0 ? `${erstes[0] ?? ""}${letztes[0] ?? ""}` : erstes.slice(0, 2).join("");
  return zeichen.toUpperCase() || "?";
}

/** Der runde Kreis mit den Initialen – groß bei der Frage, klein bei einer Antwort. */
function Kreis({ name, klein = false }: { name: string; klein?: boolean }) {
  return (
    <span className={`frage-avatar${klein ? " klein" : ""}`} aria-hidden="true">
      {initialen(name)}
    </span>
  );
}

/**
 * Das Textfeld wächst mit dem Text. Chrome kann das selbst (`field-sizing`,
 * marke.css), Safari am iPhone nicht – dort rechnet es hier nach.
 */
function useMitwachsen(text: string) {
  const feld = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = feld.current;
    if (!el || (typeof CSS !== "undefined" && CSS.supports("field-sizing", "content"))) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);
  return feld;
}

// --- Eine Frage -------------------------------------------------------------------------------

/** Wer: „Du“ für mich, sonst der Name, wie das Büro ihn kennt. */
function wer(von: NutzerKurz, ich: NutzerKurz): string {
  return von.id === ich.id ? "Du" : von.name;
}

/**
 * Rechts im Kopf: „offen“ in Lime, „erledigt“ grau, „wartet“, solange die
 * Frage noch am Gerät liegt. Wartet sie auf mich, sagt der Chip in Lime,
 * was zu tun ist: „bitte antworten“.
 */
function StandChip({ frage, anMir }: { frage: FrageAmGeraet; anMir: boolean }) {
  if (frage.wartet) {
    return (
      <Chip art="warn" ikon="uhr">
        wartet
      </Chip>
    );
  }
  if (frage.stand === "erledigt") {
    return (
      <Chip ikon="haken">
        erledigt
      </Chip>
    );
  }
  return <Chip art="lime">{anMir ? "bitte antworten" : "offen"}</Chip>;
}

/**
 * Eine Frage mit ihren Antworten und dem, was man tun kann: offen –
 * „Antworten“ (Senden) und „Erledigt“; erledigt – wer, wann, und „wieder
 * öffnen“. `wo`: wenn sie nicht an ihrem Stück steht (oben an der
 * Besichtigung, weil das Gerät das Stück nicht kennt).
 */
function FrageKarte({ frage, ich, aufnahmeId, wo }: { frage: FrageAmGeraet; ich: NutzerKurz; aufnahmeId: string; wo?: string }) {
  const [text, setText] = useState("");
  const [arbeitet, setArbeitet] = useState(false);
  const karte = useRef<HTMLDivElement>(null);
  const antwortFeld = useMitwachsen(text);
  const anMir = wartetAufMich(frage, ich.id);
  const eigene = frage.von.id === ich.id;

  // Das Band hat hierher geführt: in die Mitte rollen – kurz nach dem Sprung der Seite nach oben.
  useEffect(() => {
    if (ziel !== frage.id) return;
    const uhr = window.setTimeout(() => {
      if (ziel !== frage.id) return;
      ziel = null;
      karte.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 60);
    return () => window.clearTimeout(uhr);
  }, [frage.id]);

  async function senden() {
    setArbeitet(true);
    try {
      const id = await antworten(aufnahmeId, frage.id, text, ich);
      if (id) {
        setText("");
        toastZeigen("Antwort gespeichert – geht ans Büro");
      } else {
        toastZeigen("Antworten geht hier gerade nicht.", "fehler");
      }
    } catch (fehler) {
      speichernFehlgeschlagen(fehler);
    } finally {
      setArbeitet(false);
    }
  }

  async function standSetzen(stand: "erledigt" | "offen") {
    setArbeitet(true);
    try {
      if (await frageStandAendern(aufnahmeId, frage.id, stand, ich)) toastZeigen(stand === "erledigt" ? "Erledigt" : "Wieder offen");
    } catch (fehler) {
      speichernFehlgeschlagen(fehler);
    } finally {
      setArbeitet(false);
    }
  }

  const feld = `antwort-${frage.id}`;
  const erledigt = frage.stand === "erledigt";
  return (
    <div
      id={`frage-${frage.id}`}
      ref={karte}
      className={`frage-karte ${erledigt ? "erledigt" : "offen"}${anMir ? " an-mir" : ""}`}
      role="group"
      aria-label={eigene ? "Deine Frage" : `Frage von ${frage.von.name}`}
    >
      <div className="frage-kopf">
        <Kreis name={frage.von.name} />
        <span className="frage-wer">
          <b>{wer(frage.von, ich)}</b>
          <span className="frage-zeit">{wannZeigen(frage.gestelltAm)}</span>
        </span>
        <StandChip frage={frage} anMir={anMir} />
      </div>
      {wo && <span className="frage-wo">{wo}</span>}
      <p className="frage-text">{frage.text}</p>

      {frage.antworten.length > 0 && (
        <ul className="frage-antworten" aria-label="Antworten">
          {frage.antworten.map((x) => (
            <li key={x.id}>
              <Kreis name={x.von.name} klein />
              <div className="frage-antwort">
                <span className="frage-wer">
                  <b>{wer(x.von, ich)}</b>
                  <span className="frage-zeit">{wannZeigen(x.geantwortetAm)}</span>
                  {x.wartet && (
                    <Chip art="warn" ikon="uhr">
                      wartet
                    </Chip>
                  )}
                </span>
                <p className="frage-text">{x.text}</p>
              </div>
            </li>
          ))}
        </ul>
      )}

      {erledigt ? (
        <div className="frage-erledigt">
          <span className="frage-erledigt-wort">
            <Ikon name="haken" groesse="k" />
            Erledigt
            {frage.erledigtVon ? ` von ${frage.erledigtVon.id === ich.id ? "dir" : frage.erledigtVon.name}` : ""}
            {frage.erledigtAm ? ` · ${wannZeigen(frage.erledigtAm)}` : ""}
          </span>
          {frage.standWartet && (
            <Chip art="warn" ikon="uhr">
              wartet
            </Chip>
          )}
          <button type="button" className="knopf-leise" disabled={arbeitet} onClick={() => void standSetzen("offen")}>
            wieder öffnen
          </button>
        </div>
      ) : (
        <div className="frage-antworten-feld">
          <label className="text-karte" htmlFor={feld}>
            <textarea
              ref={antwortFeld}
              id={feld}
              rows={2}
              maxLength={BUERO_GRENZEN.frageText}
              value={text}
              aria-label="Deine Antwort"
              placeholder="Antworten – tippen oder diktieren"
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <DiktatHilfe />
          <div className="knopf-reihe">
            <button type="button" className="knopf-rand" disabled={arbeitet} onClick={() => void standSetzen("erledigt")}>
              <Ikon name="haken" /> Erledigt
            </button>
            <button type="button" className="knopf-haupt" disabled={arbeitet || gespraechText(text) === null} onClick={() => void senden()}>
              <Ikon name="senden" /> Senden
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Frage stellen ------------------------------------------------------------------------------

/**
 * „Frage stellen“ an der Besichtigung, einer Station oder einem Foto: zu ein
 * Knopf, offen ein Feld mit „Senden“. Der Knopf sagt immer nur „Frage
 * stellen“; `wo` („zu diesem Foto“) steht in seinem Namen für Vorlese-
 * programme und offen in der Überschrift – das Büro sieht die Frage genau dort.
 */
export function FrageStellen({ aufnahmeId, bezug, ich, wo }: { aufnahmeId: string; bezug: { tabelle: FrageBezug; id: string }; ich: NutzerKurz; wo: string }) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState("");
  const [arbeitet, setArbeitet] = useState(false);
  const frageFeld = useMitwachsen(text);

  async function senden() {
    setArbeitet(true);
    try {
      const id = await frageStellen(aufnahmeId, bezug, text, ich);
      if (id) {
        setText("");
        setOffen(false);
        toastZeigen("Frage gespeichert – geht ans Büro");
      } else {
        toastZeigen("Fragen geht hier erst, wenn die Besichtigung gesendet ist.", "fehler");
      }
    } catch (fehler) {
      speichernFehlgeschlagen(fehler);
    } finally {
      setArbeitet(false);
    }
  }

  if (!offen) {
    return (
      <button type="button" className="frage-stellen" aria-label={`Frage ${wo} stellen`} onClick={() => setOffen(true)}>
        {/* Am Knopf nur das Fragezeichen, ohne Kreis (Gernot beim Nachtest, 22.10.2026). */}
        <span className="frage-stellen-zeichen" aria-hidden="true">
          <Ikon name="fragezeichen" />
        </span>
        Frage stellen
      </button>
    );
  }
  const feld = `frage-neu-${bezug.tabelle}-${bezug.id}`;
  return (
    <div className="frage-neu" role="group" aria-labelledby={`${feld}-titel`}>
      <div className="frage-neu-kopf">
        <FrageZeichen />
        <b id={`${feld}-titel`}>Frage ans Büro {wo}</b>
      </div>
      <label className="text-karte" htmlFor={feld}>
        <textarea
          ref={frageFeld}
          id={feld}
          rows={3}
          // Ausdrücklich „Frage stellen“ getippt: gleich hinein.
          autoFocus
          maxLength={BUERO_GRENZEN.frageText}
          value={text}
          aria-label={`Deine Frage ${wo}`}
          placeholder="Deine Frage – tippen oder diktieren"
          onChange={(e) => setText(e.target.value)}
        />
      </label>
      <DiktatHilfe />
      <div className="knopf-reihe">
        <button
          type="button"
          className="knopf-rand"
          disabled={arbeitet}
          onClick={() => {
            setOffen(false);
            setText("");
          }}
        >
          Abbrechen
        </button>
        <button type="button" className="knopf-haupt" disabled={arbeitet || gespraechText(text) === null} onClick={() => void senden()}>
          <Ikon name="senden" /> Senden
        </button>
      </div>
    </div>
  );
}

// --- Die Fragen an einem Stück ----------------------------------------------------------------

/**
 * Die Fragen an einem Stück, älteste zuerst, und – wenn dort gefragt werden
 * kann – „Frage stellen“. Ohne Fragen und ohne „Frage stellen“: nichts.
 * `titel`: die Überschrift, nur wenn es Fragen gibt. `woJeFrage`: bei
 * Fragen, die nicht an ihrem Stück stehen, wo sie eigentlich hingehören.
 */
export function FragenAbschnitt({
  fragen,
  ich,
  aufnahmeId,
  titel,
  stellen,
  woJeFrage,
}: {
  fragen: readonly FrageAmGeraet[];
  ich: NutzerKurz;
  aufnahmeId: string;
  titel?: string;
  /** Wo „Frage stellen“ fragt – ohne: hier wird nicht gefragt (etwa an der Notiz). */
  stellen?: { bezug: { tabelle: FrageBezug; id: string }; wo: string };
  woJeFrage?: (f: FrageAmGeraet) => string | undefined;
}) {
  if (fragen.length === 0 && !stellen) return null;
  return (
    <>
      {fragen.length > 0 && titel && <div className="abschnitt-titel">{titel}</div>}
      {fragen.length > 0 && (
        <div className="fragen-liste">
          {fragen.map((f) => (
            <FrageKarte key={f.id} frage={f} ich={ich} aufnahmeId={aufnahmeId} wo={woJeFrage?.(f)} />
          ))}
        </div>
      )}
      {stellen && <FrageStellen aufnahmeId={aufnahmeId} bezug={stellen.bezug} ich={ich} wo={stellen.wo} />}
    </>
  );
}

/** Die offenen Fragen an diesen Stücken – für die Marken an Foto und Station. */
export function offeneAn(fragen: readonly FrageAmGeraet[], passt: (f: FrageAmGeraet) => boolean): number {
  return fragen.filter((f) => f.stand === "offen" && passt(f)).length;
}
